import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@tanstack/react-query';
import type { ColumnFiltersState, PaginationState } from '@tanstack/react-table';
import { toast } from 'sonner';

import { apiAdmintargetsGetCollection } from '@r/api/generated/client.ts';
import { unparse } from 'papaparse';
import { downloadCSV } from '@r/utils/utils.ts';
import type { LogicTypeTargetStatusLogicTypeTargetStatusCollectionReadModel } from '@r/api/generated/client.schemas.ts';
import { DataTable } from '@r/components/data-table/data-table.tsx';
import { DataTableSkeleton } from '@r/components/data-table/data-table-skeleton.tsx';
import { DataTableAdvancedToolbar } from '@r/components/data-table/advanced/data-table-advanced-toolbar.tsx';
import { useDataTable } from '@r/hooks/use-data-table.ts';
import { format as formatDate } from 'date-fns';
import {
  useTargetsColumns,
  useTargetsFilterFields,
  type TargetCollection
} from '@admin/components/performances/targets-columns.tsx';
import { useUser } from '@r/api/auth.tsx';
import { getAdminTablePersistenceKey } from '@r/utils/data-table-persistence.ts';
import { isOrvalErrorFromPath } from '@r/api/orval-error-helper.ts';
import { useOrvalErrorToast } from '@r/hooks/use-orval-error-toast.ts';
import { useWorkspaceName } from '@r/hooks/use-workspace-name.ts';
import { UseWorkspace } from '@r/hooks/use-workspace.ts';
import { LogicLogicReadModelType } from '@r/api/generated/client.schemas.ts';

type TargetFilters = {
  companyName: string;
  companyUniqueCode: string;
  logicTypeTargetReference: string;
  achieved: string;
  companyNameSortValue: 'asc' | 'desc' | undefined;
  valueSortValue: 'asc' | 'desc' | undefined;
  targetValueSortValue: 'asc' | 'desc' | undefined;
  achievedPercentage: 'asc' | 'desc' | undefined;
  updatedAtSortValue: 'asc' | 'desc' | undefined;
};

type TargetsQueryResult = {
  data: TargetCollection[];
  pagination?: { last?: number; totalItems?: number };
};

export const Targets = () => {
  const { t } = useTranslation();
  const showOrvalErrorToast = useOrvalErrorToast();
  const [isExporting, setIsExporting] = useState(false);

  const authUser = useUser();
  const { sanitizedWorkspaceName } = useWorkspaceName();
  const { workspace } = UseWorkspace(authUser.data);
  const isTargetPointLogic =
    workspace?.currentLogicWorkspace?.logic.type === LogicLogicReadModelType.TARGET_POINT;

  async function targetsListFiltersQuery(
    _filters: TargetFilters,
    page: number | null = null,
    limit: number | null = null
  ): Promise<TargetsQueryResult> {
    try {
      const {
        companyName,
        companyUniqueCode,
        logicTypeTargetReference,
        achieved,
        companyNameSortValue,
        valueSortValue,
        targetValueSortValue,
        achievedPercentage,
        updatedAtSortValue
      } = _filters;

      const queriesPayload = {
        page: page ?? pageIndex + 1,
        perPage: limit ?? pageSize,
        ...(companyName ? { 'company.name': companyName } : {}),
        ...(companyUniqueCode ? { 'company.uniqueCode': companyUniqueCode } : {}),
        ...(logicTypeTargetReference
          ? { 'logicTypeTarget.reference': logicTypeTargetReference }
          : {}),
        ...(achieved ? { achieved } : {}),
        ...(companyNameSortValue ? { 'order[company.name]': companyNameSortValue } : {}),
        ...(valueSortValue ? { 'order[value]': valueSortValue } : {}),
        ...(targetValueSortValue ? { 'order[targetValue]': targetValueSortValue } : {}),
        ...(achievedPercentage ? { 'order[achievedPercentage]': achievedPercentage } : {}),
        ...(updatedAtSortValue ? { 'order[updatedAt]': updatedAtSortValue } : {})
      };

      const response = await apiAdmintargetsGetCollection(queriesPayload);

      type ApiTarget = LogicTypeTargetStatusLogicTypeTargetStatusCollectionReadModel;
      const mappedData: TargetCollection[] =
        (response?.data as ApiTarget[] | undefined)?.map((o) => ({
          id: o.id,
          company: {
            name: o.company?.name ?? null,
            uniqueCode: o.company?.uniqueCode ?? null
          },
          logicTypeTarget: {
            reference: o.logicTypeTarget?.reference ?? null,
            description: o.logicTypeTarget?.description ?? null
          },
          value: o.value ?? null,
          targetValue: o.targetValue ?? null,
          achievedPercentage: o.achievedPercentage ?? null,
          achievedAt: o.achievedAt ?? null,
          updatedAt: o.updatedAt ?? null
        })) ?? [];

      return {
        data: mappedData,
        pagination: response?.pagination
      };
    } catch (error) {
      if (isOrvalErrorFromPath(error, 'get', '/admin/targets')) {
        showOrvalErrorToast(error, t('toast.error.label'));
      } else {
        toast.error(`${t('toast.error.label')}`);
      }
      throw error;
    }
  }

  function targetsListFilters(): TargetFilters {
    const companyName =
      (columnFilters.find((filter) => filter.id === 'companyName')?.value as string) ?? '';
    const companyUniqueCode =
      (columnFilters.find((filter) => filter.id === 'companyUniqueCode')?.value as string) ?? '';
    const logicTypeTargetReference =
      (columnFilters.find((filter) => filter.id === 'logicTypeTargetReference')?.value as string) ??
      '';
    const achievedFilter = columnFilters.find((filter) => filter.id === 'achieved')?.value;
    const achieved = Array.isArray(achievedFilter)
      ? ((achievedFilter[0] as string) ?? '')
      : ((achievedFilter as string) ?? '');

    const sortValue = (id: string): 'asc' | 'desc' | undefined => {
      const sort = sorting.find((s) => s.id === id);
      return sort ? (sort.desc ? 'desc' : 'asc') : undefined;
    };

    return {
      companyName,
      companyUniqueCode,
      logicTypeTargetReference,
      achieved,
      companyNameSortValue: sortValue('companyName'),
      valueSortValue: sortValue('value'),
      targetValueSortValue: sortValue('targetValue'),
      achievedPercentage: sortValue('achievedPercentage'),
      updatedAtSortValue: sortValue('updatedAt')
    };
  }

  function getTargetDataForExport(target: TargetCollection) {
    const sanitize = (value: string | number | undefined | null) =>
      String(value ?? '').replace(/[\r\n\t]/g, '');
    const formatForExportDate = (value?: string | null) =>
      value ? formatDate(new Date(value), 'yyyy-MM-dd') : '';

    return [
      sanitize(target.company?.name),
      sanitize(target.company?.uniqueCode),
      sanitize(target.logicTypeTarget?.reference),
      sanitize(target.logicTypeTarget?.description),
      sanitize(target.value),
      sanitize(target.targetValue),
      sanitize(`${Math.round(target.achievedPercentage ?? 0)}%`),
      sanitize(target.achievedAt ? t('label.target.achieved') : t('label.target.not.achieved')),
      sanitize(formatForExportDate(target.updatedAt))
    ];
  }

  const targetsListQuery = useQuery<TargetsQueryResult>({
    queryKey: ['targets_list'],
    queryFn: async (): Promise<TargetsQueryResult> => {
      return await targetsListFiltersQuery(targetsListFilters());
    }
  });

  const filterFields = useTargetsFilterFields();

  const targetsTablePersistenceKey = useMemo(
    // Cle de persistance isolee par utilisateur pour conserver ses preferences.
    () => getAdminTablePersistenceKey('targets', authUser.data?.id),
    [authUser.data?.id]
  );

  const { table } = useDataTable<TargetCollection, unknown>({
    data: targetsListQuery.data?.data ?? [],
    columns: useTargetsColumns(isTargetPointLogic),
    filterFields,
    pageCount: targetsListQuery.data?.pagination?.last ?? 1,
    enableAdvancedFilter: true,
    persistence: {
      // Persiste pagination, tri et filtres en localStorage.
      key: targetsTablePersistenceKey
    }
  });

  const { pageIndex, pageSize }: PaginationState = table.getState().pagination as PaginationState;
  const sorting = table.getState().sorting;
  const columnFilters: ColumnFiltersState = table.getState().columnFilters;
  const prevValuesRef = useRef({ pageIndex, pageSize, columnFilters, sorting });

  async function exportData() {
    setIsExporting(true);

    let allData: TargetCollection[] = [];
    const limit = 500;

    const totalPageCount = Math.ceil((targetsListQuery.data?.pagination?.totalItems ?? 1) / limit);
    const promises = Array.from({ length: totalPageCount }, (_, i) =>
      targetsListFiltersQuery(targetsListFilters(), i + 1, limit)
    );

    const pagesData = await Promise.all(promises);
    allData = pagesData.flatMap((pageData) => pageData.data);

    const fields = [
      t('label.company.name'),
      t('label.company.unique.code'),
      t('label.performances.target.reference'),
      t('label.target.description'),
      t('label.target.value'),
      t('label.target.goal'),
      t('label.target.progress'),
      t('label.target.status'),
      t('label.target.updated.at')
    ];

    const csv = unparse({
      fields: fields,
      data: allData.map((o) => getTargetDataForExport(o))
    });

    const currentDate = new Date();
    const baseFileName = `targets-export-${currentDate.toISOString().slice(0, 19).replace(/[:T]/g, '-')}.csv`;
    const fileName = sanitizedWorkspaceName
      ? `${sanitizedWorkspaceName}_${baseFileName}`
      : baseFileName;
    downloadCSV(csv, fileName);

    setIsExporting(false);
  }

  useEffect(() => {
    const prevValues = prevValuesRef.current;
    if (
      pageIndex != prevValues.pageIndex ||
      pageSize != prevValues.pageSize ||
      JSON.stringify(columnFilters) !== JSON.stringify(prevValues.columnFilters) ||
      JSON.stringify(sorting) != JSON.stringify(prevValues.sorting)
    ) {
      targetsListQuery.refetch();
    }

    prevValuesRef.current = { pageIndex, pageSize, columnFilters, sorting };
  }, [pageIndex, pageSize, columnFilters, sorting]);

  return (
    <>
      <span className="text-4xl font-bold">{t('admin.targets.title')}</span>

      {targetsListQuery.isLoading && (
        <DataTableSkeleton columnCount={9} cellWidths={['24rem', '12rem', '12rem', '8rem']} />
      )}

      {targetsListQuery.isSuccess && (
        <>
          <DataTableAdvancedToolbar
            table={table}
            filterFields={filterFields}
            onExport={exportData}
            isExporting={isExporting}
          />

          <DataTable table={table} />
        </>
      )}
    </>
  );
};
